import { createContext, useReducer, useEffect, useCallback, useState, useRef } from "react";
import useIdleLogout from "../hooks/useIdleLogout";
// Session timings are calculated dynamically based on backend config

const LOGOUT_REASONS = {
  IDLE: "idle_timeout",
  SESSION_EXPIRED: "session_expired",
  UNAUTHORIZED: "unauthorized",
};

const getJwtExpMs = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    const payload = JSON.parse(atob(parts[1]));
    return payload?.exp ? payload.exp * 1000 : null;
  } catch (err) {
    return null;
  }
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch (err) {
    return null;
  }
};

const parseJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch (err) {
    return null;
  }
};

const redirectToLogin = (reason) => {
  if (reason) {
    sessionStorage.setItem("auth_logout_reason", reason);
  } else {
    sessionStorage.removeItem("auth_logout_reason");
  }
  // The password-reset pages are for someone with no session; sending them to
  // /login is exactly what must not happen there.
  if (/^\/(page-forgot-password|reset-password\/)/.test(window.location.pathname)) return;
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

const mergeSessionMeta = (user, sessionMeta = {}) => ({
  ...user,
  sessionExpiryMs:
    sessionMeta.sessionExpiryMs ?? user?.sessionExpiryMs ?? null,
  idleExpiryMs: sessionMeta.idleExpiryMs ?? user?.idleExpiryMs ?? null,
  idleTimeoutMs: sessionMeta.idleTimeoutMs ?? user?.idleTimeoutMs ?? null,
});

// Reducer to manage auth state
export const authReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN":
      return { user: action.payload };
    case "LOGOUT":
      return { user: null };
    case "UPDATE_USER":
      return { ...state, user: action.payload };
    default:
      return state;
  }
};

// Create context
export const AuthContext = createContext();

// Provider component
export const AuthContextProvider = ({ children }) => {
  const [sessionNotice, setSessionNotice] = useState(null);
  const lastAutoExtendAtRef = useRef(0);
  const autoExtendInFlightRef = useRef(false);

  // Initialize from localStorage for persistence across refresh
  const [state, dispatch] = useReducer(authReducer, {
    user: getStoredUser(),
  });

  const logoutWithReason = useCallback(
    (reason = LOGOUT_REASONS.UNAUTHORIZED) => {
      const hadActiveSession = Boolean(getStoredUser()?.token);
      localStorage.removeItem("user");
      dispatch({ type: "LOGOUT" });
      setSessionNotice(null);
      redirectToLogin(hadActiveSession ? reason : null);
    },
    [dispatch],
  );

  const upsertSessionMeta = useCallback((sessionMeta = {}) => {
    const existingUser = getStoredUser();
    if (!existingUser) return;
    const updatedUser = mergeSessionMeta(existingUser, sessionMeta);
    const unchanged =
      existingUser.sessionExpiryMs === updatedUser.sessionExpiryMs &&
      existingUser.idleExpiryMs === updatedUser.idleExpiryMs &&
      existingUser.idleTimeoutMs === updatedUser.idleTimeoutMs;
    if (unchanged) return;
    localStorage.setItem("user", JSON.stringify(updatedUser));
    dispatch({ type: "LOGIN", payload: updatedUser });
  }, [dispatch]);

  // On mount, validate user from API
  useEffect(() => {
    const validateUser = async () => {
      try {
        const response = await fetch("/api/superadmin/getUser");
        const json = await parseJsonSafely(response);

        if (response.ok && json?.user) {
          const updatedUser = {
            ...json.user,
            token: json.token || null,
            sessionExpiryMs: json.sessionExpiryMs || null,
            idleExpiryMs: json.idleExpiryMs || null,
            idleTimeoutMs: json.idleTimeoutMs || null,
          };

          // Save back to localStorage
          localStorage.setItem("user", JSON.stringify(updatedUser));

          // Update context state
          dispatch({ type: "LOGIN", payload: updatedUser });
        } else {
          throw new Error(LOGOUT_REASONS.UNAUTHORIZED);
        }
      } catch (err) {
        logoutWithReason(LOGOUT_REASONS.UNAUTHORIZED);
      }
    };

    validateUser();
  }, [logoutWithReason]);

  // Keep access token fresh via rotating refresh token flow.
  useEffect(() => {
    if (!state.user?.token) return;

    let refreshTimeout = null;
    let cancelled = false;

    const scheduleNextRefresh = (token) => {
      const expMs = getJwtExpMs(token);
      if (!expMs) {
        // fallback when token can't be parsed: try again in 10s
        refreshTimeout = setTimeout(refreshAuth, 10000);
        return;
      }

      const remainingMs = expMs - Date.now();
      // Dynamically calculate leeway: 20% of remaining time, max 1 minute, min 10 seconds.
      const leewayMs = Math.min(60000, Math.max(10000, Math.floor(remainingMs * 0.2)));
      const delay = Math.max(0, remainingMs - leewayMs);
      refreshTimeout = setTimeout(refreshAuth, delay);
    };

    const refreshAuth = async () => {
      try {
        const response = await fetch("/api/superadmin/refresh", {
          method: "POST",
        });
        const json = await parseJsonSafely(response);

        if (!response.ok || !json?.token) {
          const errorText = String(json?.error || "");
          const isExpired =
            response.status === 401 &&
            (errorText.toLowerCase().includes("refresh") ||
              errorText.toLowerCase().includes("expired"));
          throw new Error(
            isExpired
              ? LOGOUT_REASONS.SESSION_EXPIRED
              : LOGOUT_REASONS.UNAUTHORIZED,
          );
        }

        if (cancelled) return;

        const refreshedUser = {
          ...state.user,
          ...json,
          token: json.token,
          sessionExpiryMs: json.sessionExpiryMs || state.user?.sessionExpiryMs || null,
          idleExpiryMs: json.idleExpiryMs || state.user?.idleExpiryMs || null,
          idleTimeoutMs: json.idleTimeoutMs || state.user?.idleTimeoutMs || null,
        };

        localStorage.setItem("user", JSON.stringify(refreshedUser));
        dispatch({ type: "LOGIN", payload: refreshedUser });
        setSessionNotice(null);

        scheduleNextRefresh(refreshedUser.token);
      } catch (err) {
        const reason =
          err?.message === LOGOUT_REASONS.SESSION_EXPIRED
            ? LOGOUT_REASONS.SESSION_EXPIRED
            : LOGOUT_REASONS.UNAUTHORIZED;
        logoutWithReason(reason);
      }
    };

    scheduleNextRefresh(state.user.token);

    return () => {
      cancelled = true;
      clearTimeout(refreshTimeout);
    };
  }, [state.user?.token, state.user?.sessionExpiryMs, logoutWithReason]);

  // Warn users before absolute session expiry and force re-auth when max session ends.
  useEffect(() => {
    if (!state.user?.sessionExpiryMs) return;

    const sessionExpiryMs = Number(state.user.sessionExpiryMs);
    if (!Number.isFinite(sessionExpiryMs)) return;

    const now = Date.now();
    // Dynamically calculate warning time: 10% of total session time, max 5 minutes, min 30 seconds.
    const remainingSessionMs = sessionExpiryMs - now;
    const warnSeconds = Math.min(300, Math.max(30, Math.floor((remainingSessionMs / 1000) * 0.1)));
    const warnDelayMs = sessionExpiryMs - now - warnSeconds * 1000;
    const expireDelayMs = sessionExpiryMs - now;
    let warnTimer = null;
    let expireTimer = null;

    const getMinutesLeft = () =>
      Math.max(1, Math.ceil((sessionExpiryMs - Date.now()) / (60 * 1000)));

    if (warnDelayMs > 0) {
      warnTimer = setTimeout(() => {
        setSessionNotice({
          type: "warning",
          message: `For security, this session will end in about ${getMinutesLeft()} minute(s). Save your work. You'll need to log in again.`,
        });
      }, warnDelayMs);
    } else if (expireDelayMs > 0) {
      setSessionNotice({
        type: "warning",
        message:
          "For security, this session is about to end soon. Save your work. You'll need to log in again.",
      });
    }

    if (expireDelayMs > 0) {
      expireTimer = setTimeout(() => {
        logoutWithReason(LOGOUT_REASONS.SESSION_EXPIRED);
      }, expireDelayMs);
    } else {
      logoutWithReason(LOGOUT_REASONS.SESSION_EXPIRED);
    }

    return () => {
      clearTimeout(warnTimer);
      clearTimeout(expireTimer);
    };
  }, [state.user?.sessionExpiryMs, logoutWithReason]);

  // Auto-logout after 30 minutes of inactivity (60s warning before logout)
  const handleIdleLogout = useCallback(() => {
    logoutWithReason(LOGOUT_REASONS.IDLE);
  }, [logoutWithReason]);

  const extendIdleSession = useCallback(async ({ logoutOnFailure = false } = {}) => {
    try {
      const response = await fetch("/api/superadmin/session/extend", {
        method: "POST",
      });
      const json = await parseJsonSafely(response);

      if (!response.ok || !json?.success) {
        if (logoutOnFailure) {
          logoutWithReason(LOGOUT_REASONS.IDLE);
        }
        return false;
      }

      upsertSessionMeta({
        sessionExpiryMs: json.sessionExpiryMs || null,
        idleExpiryMs: json.idleExpiryMs || null,
        idleTimeoutMs: json.idleTimeoutMs || null,
      });
      return true;
    } catch (err) {
      if (logoutOnFailure) {
        logoutWithReason(LOGOUT_REASONS.IDLE);
      }
      return false;
    }
  }, [logoutWithReason, upsertSessionMeta]);

  // Auto-extend idle session on real user activity (throttled).
  useEffect(() => {
    if (!state.user?.token) return undefined;

    const maybeAutoExtend = async () => {
      const now = Date.now();
      // Dynamically calculate throttle: 1/6th of total idle timeout, min 10 seconds.
      const totalIdleTimeoutMs = Number(state.user?.idleTimeoutMs) || 1800000;
      const throttleMs = Math.max(10000, Math.floor(totalIdleTimeoutMs / 6));

      if (now - lastAutoExtendAtRef.current < throttleMs) return;
      if (autoExtendInFlightRef.current) return;

      autoExtendInFlightRef.current = true;
      lastAutoExtendAtRef.current = now;
      try {
        await extendIdleSession({ logoutOnFailure: false });
      } finally {
        autoExtendInFlightRef.current = false;
      }
    };

    const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll", "click"];
    const onActivity = () => {
      maybeAutoExtend();
    };

    events.forEach((eventName) =>
      window.addEventListener(eventName, onActivity, { passive: true }),
    );

    return () => {
      events.forEach((eventName) =>
        window.removeEventListener(eventName, onActivity),
      );
    };
  }, [state.user?.token, extendIdleSession]);

  const totalIdleTimeoutMs = Number(state.user?.idleTimeoutMs) || 1800000;
  const idleTimeoutMinutes = Math.max(
    1,
    Math.ceil(totalIdleTimeoutMs / (60 * 1000))
  );
  // Dynamically calculate idle warning: 20% of idle timeout, max 60s, min 15s.
  const idleWarnSeconds = Math.min(60, Math.max(15, Math.floor((totalIdleTimeoutMs / 1000) * 0.2)));

  useIdleLogout(
    handleIdleLogout,
    idleTimeoutMinutes,
    idleWarnSeconds,
    Boolean(state.user?.token),
    () => extendIdleSession({ logoutOnFailure: true }),
    state.user?.idleExpiryMs,
  );

  return (
    <AuthContext.Provider value={{ ...state, dispatch }}>
      {sessionNotice?.message && (
        <div
          className="alert alert-warning mb-0 text-center"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            borderRadius: 0,
          }}
        >
          {sessionNotice.message}
          <button
            type="button"
            className="btn-close ms-2"
            aria-label="Close"
            onClick={() => setSessionNotice(null)}
          />
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};
